import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, message, Skeleton } from 'antd';
import { DrawerPage } from 'components/DrawerPage';
import { AvatarText } from 'components/Shared/AvatarText';
import { NftPurchaseFail } from 'components/Shared/NftPaymentStatus/__partials/NftPurchaseFail';
import { NftPurchaseSuccess } from 'components/Shared/NftPaymentStatus/__partials/NftPurchaseSuccess';
import { Form, useForm } from 'components/UI/Form';
import { useMediaLoaded } from 'hooks/generic/useMediaLoaded';
import { useSearchParams } from 'hooks/useSearchParams';
import * as Styled from './styles';
import { useAppDispatch, useAppSelector } from 'appRedux/hooks';
import { getNftById, getPaymentRedirectUrl } from 'appRedux/restApi';
import { setSession } from 'services/ApiClient';
import { getPath } from 'utils';
import { addQuery } from 'utils/generic/query-route';
import { AccountQueryKey } from 'constants/account/account-route-keys';
import { EPaymentStatus } from 'constants/account/payment-method';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import { NFT_CHECKOUT_PATTERN } from 'constants/app/route-reserved-words';
import { RestAxiosError } from 'interfaces';

export const FTNFTAmCheckout = () => {
  const [form] = useForm();
  const { mediaLoaded, onMediaLoad } = useMediaLoaded();

  const searchParams = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const redirectTimeout = useRef<NodeJS.Timeout>();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const nftId = useMemo(() => {
    const [, _id] = pathname.match(NFT_CHECKOUT_PATTERN) || [null, null];
    return _id ? parseInt(_id) : undefined;
  }, [pathname]);
  const { current: nftData } = useAppSelector(({ nfts }) => nfts);

  const {
    refetch,
    data: paymentRedirectData,
    error
  } = useQuery<
    Awaited<ReturnType<typeof getPaymentRedirectUrl>>,
    RestAxiosError
  >({
    queryKey: [ReactQueryKeys.CHECKOUT, ReactQueryKeys.CHECKOUT_AM],
    queryFn: () =>
      getPaymentRedirectUrl(nftId, {
        use_shipping_address: 0,
        success_url: `${window.location.origin}${window.location.pathname}${
          addQuery({
            [AccountQueryKey.paymentStatus.value]:
              AccountQueryKey.paymentStatus.option[EPaymentStatus.SUCCESS]
          }).search
        }`,
        cancel_url: `${window.location.origin}${window.location.pathname}${
          addQuery({
            [AccountQueryKey.paymentStatus.value]:
              AccountQueryKey.paymentStatus.option[EPaymentStatus.FAILED]
          }).search
        }`
      }),
    enabled: false,
    retry: false
  });

  const showNFTView = useMemo(
    () => NFT_CHECKOUT_PATTERN.test(pathname),
    [pathname]
  );

  useEffect(() => {
    if (searchParams.token) {
      setSession(searchParams.token);
      if (nftId) {
        dispatch(getNftById(nftId.toString()));
      }
      if (searchParams.paymentStatus) {
        setStatus(searchParams.paymentStatus);

        redirectTimeout.current = setTimeout(() => {
          window.location.href = getPath('/');
        }, 5000);
      } else if (nftId) {
        refetch();
      }
    }
    return () => {
      clearTimeout(redirectTimeout.current);
    };
  }, [searchParams.token, searchParams.paymentStatus, nftId]);

  const handlePurchaseClick = () => {
    form
      .validateFields()
      .then(() => {
        if (paymentRedirectData) {
          const {
            data: { redirect_url }
          } = paymentRedirectData;
          if (!redirect_url) {
            return;
          }
          window.location.href = redirect_url;
        }
      })
      .catch(({ errorFields }) => {
        const firstErrorField = errorFields[0].name[0];
        form.scrollToField(firstErrorField, {
          behavior: 'smooth',
          scrollMode: 'always',
          block: 'center'
        });
      });
  };

  useEffect(() => {
    if (error) {
      message.error(error?.response?.data?.message);
    }
  }, [error]);

  return (
    <DrawerPage
      open={showNFTView}
      closable={false}
      headerStyle={{
        zIndex: 1,
        position: 'fixed',
        top: 0,
        right: 0,
        background: 'transparent',
        padding: '20px 20px 0'
      }}
      bodyStyle={{
        overflow: 'auto'
      }}
    >
      <Suspense>
        <Styled.Wrapper>
          <Styled.Title>{t('nft.finalCheckout')}</Styled.Title>

          <Styled.CartTileWrapper>
            {!mediaLoaded && (
              <Skeleton.Image
                active
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  gridArea: 'media'
                }}
              />
            )}
            <Styled.NftMedia
              src={
                nftData.data?.preview?.small || nftData.data?.media?.small || ''
              }
              onLoad={onMediaLoad}
              loading="lazy"
              $mediaLoaded={mediaLoaded}
            />
            <Styled.CartTileInfo>
              <AvatarText text={nftData.data?.created_by?.user_name} />
              <Styled.CartTileTitle>{nftData.data?.name}</Styled.CartTileTitle>
            </Styled.CartTileInfo>
            <Styled.CartTilePriceWrapper>
              <Styled.CartTilePriceLabel>
                {t('nft.price')}
              </Styled.CartTilePriceLabel>
              <Styled.CartTilePrice>
                {nftData.data?.formatted_price}
              </Styled.CartTilePrice>
            </Styled.CartTilePriceWrapper>
          </Styled.CartTileWrapper>
          {status ? (
            <Styled.EvocaSuccessWrapper>
              {status === 'success' ? (
                <NftPurchaseSuccess />
              ) : (
                <NftPurchaseFail />
              )}
            </Styled.EvocaSuccessWrapper>
          ) : (
            <Styled.CartFooterWrapper>
              <Styled.PaymentWrapper>
                <Form form={form}>
                  <Styled.FormItemWide
                    name="payment_agreement"
                    initialValue={false}
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.reject(
                              t('account.requiredReadAgreeTermsAndConditions')
                            );
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Checkbox>
                      <Styled.Label>
                        {t('account.paymentAgreement')}
                      </Styled.Label>
                    </Checkbox>
                  </Styled.FormItemWide>
                </Form>
              </Styled.PaymentWrapper>

              <Styled.PurchaseButton
                block
                btnheight={48}
                {...(!error?.response?.data?.error_code && {
                  onClick: handlePurchaseClick
                })}
                disabled={!!error?.response?.data?.error_code}
              >
                {t('account.payByCard')}
              </Styled.PurchaseButton>
            </Styled.CartFooterWrapper>
          )}
        </Styled.Wrapper>
      </Suspense>
    </DrawerPage>
  );
};
